import React, { useEffect, useState } from "react";

/**
 * GenerationOverlay — barra di caricamento full-screen per le generazioni
 * lunghe (PDF brochure, reel, social pack, foto AI).
 *
 * Strategia "smart fake progress":
 *  • Stati `idle` / `running` / `done` controllati dal caller via props.
 *  • Mentre `running`: la barra avanza con curva ease-out fino al 92%,
 *    senza mai raggiungere 100% (l'utente non vede mai una barra "ferma
 *    a 100% in attesa").
 *  • Quando il caller setta `done=true`: jump a 100% in 300ms, poi fade out
 *    automatico dopo 500ms.
 *  • Step opzionale (`step` prop) → mostrato sotto come "stato corrente".
 *
 * Niente emoji robot. Copy gentile, italiano, parlato.
 */
export default function GenerationOverlay({
  open,
  done = false,
  estimateMs = 8000,
  title = "Sto generando…",
  step = "",
  hint = "",
  onClose,
}) {
  const [pct, setPct] = useState(0);
  const [visible, setVisible] = useState(false);

  // Show / hide
  useEffect(() => {
    if (open) {
      setVisible(true);
      setPct(0);
    } else {
      // fade out grace
      const t = setTimeout(() => setVisible(false), 400);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Fake progress
  useEffect(() => {
    if (!open || done) return;
    const start = performance.now();
    let raf = 0;
    const tick = () => {
      const t = performance.now() - start;
      // ease-out cubic: cap 92%
      const norm = Math.min(1, t / estimateMs);
      const eased = 1 - Math.pow(1 - norm, 3);
      const next = Math.min(92, eased * 92);
      setPct(next);
      if (next < 92) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [open, done, estimateMs]);

  // Done: jump to 100% then fade
  useEffect(() => {
    if (done && open) {
      setPct(100);
      const t = setTimeout(() => {
        onClose?.();
      }, 800);
      return () => clearTimeout(t);
    }
  }, [done, open, onClose]);

  if (!visible) return null;

  return (
    <div
      data-testid="generation-overlay"
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{
        background: "rgba(28, 28, 26, 0.72)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        opacity: open ? 1 : 0,
        transition: "opacity 350ms ease",
        pointerEvents: open ? "auto" : "none",
      }}
    >
      <div
        className="bg-[#FAFAF7] rounded-2xl shadow-2xl"
        style={{
          width: "min(440px, 88vw)",
          padding: "32px 28px 28px",
          transform: open ? "scale(1)" : "scale(0.95)",
          transition: "transform 350ms cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        {/* Icona / glyph: casetta-P del brand, NON robot */}
        <div className="flex justify-center mb-4">
          <div
            className="relative flex items-center justify-center rounded-full"
            style={{
              width: 56,
              height: 56,
              background: "rgba(124, 139, 110, 0.12)",
            }}
            data-testid="generation-overlay-icon"
          >
            {/* House SVG, breathing animation */}
            <svg
              viewBox="0 0 48 48"
              width="28"
              height="28"
              fill="none"
              style={{
                animation: "prontoBreathe 1.8s ease-in-out infinite",
              }}
            >
              <path
                d="M 4 22 L 24 6 L 44 22 L 44 42 L 4 42 Z"
                stroke="#7C8B6E"
                strokeWidth="2.6"
                strokeLinejoin="round"
                fill="none"
              />
              <path
                d="M 17 32 L 17 18 L 25 18 Q 31 18 31 23 Q 31 28 25 28 L 17 28"
                stroke="#7C8B6E"
                strokeWidth="2.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
            <style>{`
              @keyframes prontoBreathe {
                0%, 100% { transform: scale(1); opacity: 0.85; }
                50%      { transform: scale(1.08); opacity: 1; }
              }
              @keyframes prontoShimmer {
                0%   { background-position: -200% 0; }
                100% { background-position:  200% 0; }
              }
            `}</style>
          </div>
        </div>

        {/* Title */}
        <div
          className="text-center"
          style={{
            fontFamily: 'ui-serif, "DM Serif Display", Georgia, serif',
            fontSize: 22,
            color: "#1C1C1A",
            letterSpacing: "-0.01em",
            lineHeight: 1.2,
            marginBottom: 6,
          }}
          data-testid="generation-overlay-title"
        >
          {title}
        </div>

        {/* Step (opzionale) */}
        <div
          className="text-center"
          style={{
            fontFamily: "ui-sans-serif, system-ui, -apple-system, sans-serif",
            fontSize: 13,
            color: "rgba(28, 28, 26, 0.6)",
            lineHeight: 1.4,
            marginBottom: 24,
            minHeight: 18,
            transition: "opacity 200ms ease",
          }}
          data-testid="generation-overlay-step"
        >
          {step || "Preparazione…"}
        </div>

        {/* Progress bar */}
        <div
          className="relative overflow-hidden"
          style={{
            background: "rgba(28, 28, 26, 0.08)",
            height: 8,
            borderRadius: 999,
          }}
        >
          <div
            data-testid="generation-overlay-progress-fill"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              bottom: 0,
              width: `${pct}%`,
              borderRadius: 999,
              background: "linear-gradient(90deg, #7C8B6E 0%, #B5C4A6 50%, #7C8B6E 100%)",
              backgroundSize: "200% 100%",
              animation: "prontoShimmer 2.2s linear infinite",
              transition: "width 320ms cubic-bezier(0.16, 1, 0.3, 1)",
            }}
          />
        </div>

        {/* Percentage + hint */}
        <div
          className="flex justify-between items-center mt-3"
          style={{
            fontFamily: "ui-sans-serif, system-ui, -apple-system, sans-serif",
            fontSize: 11,
            color: "rgba(28, 28, 26, 0.5)",
            letterSpacing: "0.04em",
          }}
        >
          <span data-testid="generation-overlay-pct">{Math.round(pct)}%</span>
          <span>{hint || "Pochi secondi…"}</span>
        </div>
      </div>
    </div>
  );
}
